import { ElementRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { CustomFormControlKeyValue } from './custom-form-control-key-value';

export abstract class CustomFormControl<T> implements ControlValueAccessor, Validators {
  private readonly disabledStatusChange = new BehaviorSubject<boolean>(false);
  private readonly invalidStatusChange = new Subject<boolean>();
  private readonly touchedStatusChange = new Subject<boolean>();

  protected readonly invalidStatus$ = this.invalidStatusChange
    .asObservable()
    .pipe(distinctUntilChanged());
  protected readonly touchedStatus$ = this.touchedStatusChange
    .asObservable()
    .pipe(distinctUntilChanged());

  // If this form control is created with some validators,
  // this observable will emit every time when one or more of those validators fail
  // and this form control is touched
  protected readonly validationErrorOnTouched$ = combineLatest([
    this.invalidStatus$,
    this.touchedStatus$,
    // A disabled form control doesn't show errors.
    // So when a form control is disabled/enabled,
    // we have to emit the error again if any.
    this.disabledStatusChange.asObservable(),
  ]).pipe(map(([invalid, touched, _]) => (invalid && touched ? { error: true } : null)));

  private onFormControlChange?: (value: T | undefined) => void;
  private onFormControlTouched?: () => void;
  private onFormControlValidatorChange?: () => void;

  constructor(private readonly thisElementRef: ElementRef) {}

  writeValue(value: T | CustomFormControlKeyValue | undefined): void {
    this.onWriteValue(value);
  }

  registerOnChange(fn: (value: T | undefined) => void): void {
    this.onFormControlChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onFormControlTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.onDisable(isDisabled);
    this.disabledStatusChange.next(isDisabled);
  }

  validate(formControl: AbstractControl<any, any>): ValidationErrors | null {
    return this.onValidate(formControl);
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onFormControlValidatorChange = fn;
  }

  protected emitFormControlChange(value: T | undefined) {
    if (this.onFormControlChange) {
      this.onFormControlChange(value);
    }
  }

  protected emitFormControlTouched() {
    if (this.onFormControlTouched) {
      this.onFormControlTouched();
    }
  }

  protected listenToStatusChange() {
    const observer = new MutationObserver((mutationRecords: MutationRecord[]) => {
      mutationRecords.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const invalid = this.thisElementRef.nativeElement.className.includes('ng-invalid');
          this.invalidStatusChange.next(invalid);

          const touched = this.thisElementRef.nativeElement.className.includes('ng-touched');
          this.touchedStatusChange.next(touched);
        }
      });
    });

    observer.observe(this.thisElementRef.nativeElement, {
      attributeFilter: ['class'],
    });
  }

  protected validateFormControl() {
    if (this.onFormControlValidatorChange) {
      this.onFormControlValidatorChange();
    }
  }

  protected abstract onWriteValue(value: T | CustomFormControlKeyValue | undefined): void;
  protected abstract onDisable(disabled: boolean): void;
  protected abstract onValidate(formControl: AbstractControl<any, any>): ValidationErrors | null;
}
